/**
 * Decide QUAIS abas o DialogoDeEdicaoDeTitulo mostra.
 *
 * O corpo aqui é uma cópia da cadeia de `if / else if` que estava dentro do computed
 * `abasPermitidas` de src/views/application/titulos/DialogoDeEdicaoDeTitulo.vue. A ordem dos
 * ramos, as comparações e os ramos inalcançáveis foram preservados como estavam: isso é um
 * congelamento do comportamento atual, não uma limpeza. Quem quiser corrigir as armadilhas
 * anotadas abaixo tem que fazer isso de propósito, num ticket próprio, porque cada uma delas muda
 * o que o usuário vê.
 *
 * O efeito colateral continua no componente: lá o resultado desta função é gravado em `this.tabs`
 * de dentro do computed. Aqui não há estado nem `this`.
 *
 * ARMADILHA DO ACENTO
 * O primeiro ramo aceita 'Crediário' (com acento) e 'Crediario' (sem). Os ramos seguintes só
 * comparam com 'Crediario' sem acento. Consequência real: com fluxo 'Saída', ação 'edicao' e
 * forma 'Crediário' acentuada, o título NÃO cai no ramo de crediário, cai no ramo genérico e a aba
 * 'Configuração Crediário' desaparece. E as duas grafias existem no sistema: o combo de forma de
 * pagamento do diálogo oferece 'Crediário' acentuado, enquanto `displayPagamento` converte o
 * 'CREDIARIO' que vem do backend para 'Crediario' sem acento.
 *
 * ARMADILHA DO NOME DA AÇÃO
 * 'criacao' e 'edicao' não significam o que parecem. Quem chama `mostrarDialogo` passa 'edicao'
 * tanto para editar quanto para criar um título (é a tela do título), e passa 'criacao' quando o
 * diálogo é aberto para trabalhar as BAIXAS de um título que já existe (daí a única aba ser
 * 'Baixas'). O motivo dessa inversão de nomes não está claro no código.
 *
 * ARMADILHA DO FALLBACK
 * Quando nenhum ramo casa (acontece de verdade: `tipoDeAcao` é um array vazio até a primeira
 * chamada de `mostrarDialogo`, então `tipoDeAcao[0]` é undefined), o original não atribuía nada e
 * devolvia o valor anterior de `this.tabs`. Aqui isso virou o `return abasAtuais` do fim, que
 * devolve a MESMA referência recebida, de propósito: no componente, `this.tabs = abasAtuais` é um
 * no-op para a reatividade do Vue (o setter sai fora quando o valor é idêntico), igual a não
 * atribuir.
 */

export type TipoDeAcaoDeTitulo = 'edicao' | 'criacao'

export type FluxoDeTituloNasAbas = 'Entrada' | 'Saída'

export type AbaDoDialogoDeTitulo =
	| 'Principal'
	| 'Configuração Crediário'
	| 'Baixas'
	| 'Erros'

export type EntradaDasAbasDeTitulo = {
	/**
	 * `formulario.formaDePagamento`. Pode ser undefined em produção mesmo o tipo do model dizendo
	 * `string | null`: `displayPagamento` não tem `else` e devolve undefined para qualquer forma
	 * fora da lista dela (Permuta, Link, TEF Pix, ...).
	 */
	formaDePagamento: string | null | undefined
	/** `tipoDeAcao[0]`. undefined enquanto `mostrarDialogo` não foi chamado. */
	tipoDeAcao: TipoDeAcaoDeTitulo | undefined
	fluxo: FluxoDeTituloNasAbas | null | undefined
	temAcessoIrrestrito: boolean
	/** Abas de agora, devolvidas intactas quando nenhum ramo casa. */
	abasAtuais: AbaDoDialogoDeTitulo[]
}

export function calcularAbasDeTitulo(entrada: EntradaDasAbasDeTitulo): AbaDoDialogoDeTitulo[] {
	const { formaDePagamento, tipoDeAcao, fluxo, temAcessoIrrestrito, abasAtuais } = entrada

	if((formaDePagamento === 'Crediário' || formaDePagamento === 'Crediario' || formaDePagamento === 'Boleto') && tipoDeAcao === 'edicao'  && fluxo !== 'Saída'){
		return temAcessoIrrestrito
			? ['Principal', 'Configuração Crediário', 'Baixas', 'Erros']
			: ['Principal', 'Configuração Crediário', 'Baixas']
	// As duas metades deste OR cobrem juntas qualquer forma de pagamento, então este ramo é
	// simplesmente "tipoDeAcao === 'criacao'". É ele que torna o último ramo inalcançável.
	} else if(((formaDePagamento === 'Crediario' || formaDePagamento === 'Boleto') && tipoDeAcao === 'criacao') || (formaDePagamento !== 'Crediario' && formaDePagamento !== 'Boleto' && tipoDeAcao === 'criacao')) {
		return ['Baixas']
	// Aqui não se compara com 'Crediário' acentuado: é a armadilha do acento do topo do arquivo.
	} else if(formaDePagamento !== 'Crediario' && formaDePagamento !== 'Boleto' && tipoDeAcao === 'edicao'){
		return temAcessoIrrestrito ? ['Principal', 'Erros'] : ['Principal']
	} else if((formaDePagamento === 'Crediario' || formaDePagamento === 'Boleto') && tipoDeAcao === 'edicao'  && fluxo === 'Saída'){
		return temAcessoIrrestrito
			? ['Principal', 'Configuração Crediário', 'Erros']
			: ['Principal', 'Configuração Crediário']
	// INALCANÇÁVEL. Para chegar aqui com tipoDeAcao 'edicao' seria preciso escapar do 3º ramo, o
	// que só acontece se a forma for exatamente 'Crediario' ou 'Boleto'; e nesse caso, com fluxo
	// 'Saída', o 4º ramo já teria pegado. Mantido porque remover ramo morto é mudança de código
	// que não muda comportamento, mas apaga a intenção de quem escreveu.
	} else if(fluxo === 'Saída' && tipoDeAcao === 'edicao'){
		return temAcessoIrrestrito ? ['Principal', 'Erros'] : ['Principal']
	// INALCANÇÁVEL. O 2º ramo já pegou todo `tipoDeAcao === 'criacao'`, qualquer que seja o fluxo.
	} else if(fluxo === 'Saída' && tipoDeAcao === 'criacao'){
		return ['Baixas']
	}

	return abasAtuais
}
